// The start and session screens (03-startup-session.md, "Screens and states"). Every one says what
// is going on in plain words with one way on; nothing spins for ever.
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import logo from '../assets/logo.svg';
import { Button } from '../blocks/Button';
import { Dialog, DialogButtons } from '../blocks/Dialog';
import { Splash } from '../blocks/Page';
import { LoadState, Reference, SLOW_MS } from '../blocks/Wait';
import { describe, type Failure } from '../core/errors';
import { readSession } from '../core/session';
import { switchingTo } from '../accounts/switching';
import { initial } from '../core/format';
import { dMark } from './mark';
import { keepStart, startKind, startSince, startState, startWords } from './startLayer';
import { createPortal } from 'react-dom';
import { config } from '../core/config';
import { markSignedOut } from '../id/store';
import { signOutTabs } from '../id/tabs';
import { t } from '../i18n';
import { startOver } from '../core/worker';

const reload = () => location.reload();
const Logo = ({ size }: { size: number }) => <img src={logo} alt="" width={size} height={size} />;

/** Puts the focus on the page's main button when it appears. */
function useFocusOnShow() {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => ref.current?.focus(), []);
  return ref;
}

export type StartState = 'waiting' | 'slow' | 'unreachable';

/**
 * The start screen (03, The start screen, v1.58.48): desyr.chat's page colour and desyr's mark over the
 * whole window (the served page's own layer, `startLayer`), kept while this is shown. Its words only when
 * slow, under the mark: after 1.5 s what opens ("Opening your chats…", "Opening your circles…" after a
 * desyr ID sign-in, "Opening <Circle>…" on a switch, counted from the pick), a polite status; after 8 s the
 * slow words with Try again, from 30 s Clear cached chats too; when the server can't be reached its words,
 * not before 1.5 s. Nothing else: no frame, skeleton or picture behind it.
 */
export function StartScreen({ unreachable = false, onClearCache }: { unreachable?: boolean; onClearCache?: () => void }) {
  useLayoutEffect(() => keepStart(), []);
  const since = startSince();
  const [after, setAfter] = useState(() => performance.now() - since);
  useEffect(() => {
    // A timer may fire a hair before its time by performance.now()'s clock: it counts as on time.
    const timers = [1500, 8000, 30_000].map((at) => setTimeout(() => setAfter(Math.max(at, performance.now() - since)), at - (performance.now() - since)));
    return () => timers.forEach(clearTimeout);
  }, [since]);
  const state: StartState = after < 1500 ? 'waiting' : unreachable ? 'unreachable' : after >= 8000 ? 'slow' : 'waiting';
  useLayoutEffect(() => startState(state), [state]);
  const kind = startKind();
  const user = readSession()?.user_id;
  const circle = kind === 'switch' ? switchingTo(user)?.circle : undefined;
  const line = circle ? t('accounts.opening', { circle }) : t(kind === 'signin' ? 'id.opening' : 'start.opening');
  return createPortal(
    state === 'waiting' ? <p className="start-line" role="status">{after >= 1500 && line}</p> : <StartWords state={state} onClearCache={after >= 30_000 ? onClearCache : undefined} />,
    startWords(),
  );
}

/**
 * The start's slow or can't-reach words with their buttons, in the quiet line's place (03, Starting 3 and 4).
 * Try again starts over as a fresh open does (v1.58.76): a newer version first, page, code and settings
 * from the network, the session checked again.
 */
export function StartWords({ state, onClearCache }: { state: 'slow' | 'unreachable'; onClearCache?: () => void }) {
  return (
    <div className="start-message" role="alert">
      <p>{t(state === 'slow' ? 'start.slow' : 'start.unreachable')}</p>
      <div className="start-buttons">
        <Button variant="soft" onClick={() => void startOver()}>{t('common.tryAgain')}</Button>
        {state === 'slow' && onClearCache && (
          <Button variant="text" onClick={onClearCache}>
            {t('start.clearCache')}
          </Button>
        )}
      </div>
    </div>
  );
}

/** A circle's look in a skeleton's header: its name, its ID colour, its logo while in memory. */
export interface Look {
  name: string;
  colour: number;
  logo?: string;
}

/**
 * A skeleton's header (02, One loading language): the circle's logo or tile and name (only the
 * shapes without a look), and under the name after 1.5 s from `since` the one quiet line, or `words`
 * in its place. Hidden from screen readers but for that line.
 */
export function SkeletonHead({ look, line, words, since = 0 }: { look?: Look; line: string; words?: ReactNode; since?: number }) {
  const [shown, setShown] = useState(() => performance.now() - since >= 1500);
  useEffect(() => {
    const t = setTimeout(() => setShown(true), 1500 - performance.now() + since);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="skeleton-head">
      {look?.logo ? (
        <img src={look.logo} alt="" width={36} height={36} />
      ) : (
        <span className={look ? undefined : 'shape'} style={look && { background: `var(--name-${look.colour})` }} aria-hidden="true">
          {look && initial(look.name, 2)}
        </span>
      )}
      <div>
        {look ? <strong aria-hidden="true">{look.name}</strong> : <i className="shape" />}
        {words ?? <p role="status">{shown && line}</p>}
      </div>
    </div>
  );
}

/**
 * The list's outline: its header with the circle and the line, the rows' shapes (02, One loading
 * language): a phone's tab page while its list waits inside the frame (07).
 */
export function SkeletonList({ hook, ...head }: Parameters<typeof SkeletonHead>[0] & { hook?: Record<`data-${string}`, string | undefined> }) {
  return (
    <div className="skeleton-list" aria-busy="true" {...hook}>
      <SkeletonHead {...head} />
      {/* The opening mark under the header (02, v1.58.37); the slow words stay in the header. */}
      <MarkWait place="tab" timed={false} />
    </div>
  );
}

/**
 * Home's header row while the chats open (02, v1.58.41): its title "Home" (the circle's name on the phone's
 * Home banner, v1.58.44), under it "Opening your chats"
 * (or the start's slow words); the start's side list and the frame's draw the same, so it hands over in place.
 * A switch's "Opening <Circle>…" (`line`) only after 1.5 s from `since` (the page's start, or the pick
 * before the reload), so each header taking over keeps the time (33, Switching 5; T-switch-skeleton).
 */
export function OpeningHeader({ title = t('frame.home'), line, words, since = 0, hook }: { title?: string; line?: string; words?: ReactNode; since?: number; hook?: Record<`data-${string}`, string | undefined> }) {
  const [late, setLate] = useState(() => performance.now() - since >= 1500);
  useEffect(() => {
    const timer = setTimeout(() => setLate(true), 1500 - performance.now() + since);
    return () => clearTimeout(timer);
  }, []);
  return (
    <div className="side-header opening-header" {...hook}>
      <strong>{title}</strong>
      {words ?? (
        <p role="status">
          {(late && line) || t('start.openingChats')}
          <span className="opening-dots" aria-hidden="true"><i /><i /><i /></span>
        </p>
      )}
    </div>
  );
}

let markSince: number | undefined;
const still = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * The page card's opening mark (02, Opening in design A, v1.58.36): desyr's "d", 56 px, in the circle's
 * colour, drawn as a stroke (650 ms), filled (180 ms), then breathing until the page's content arrives
 * (`open` false), when it fades out over 200 ms; with reduce motion filled and still, gone at once. One
 * mark from the start's skeleton into the frame or a page's wait: it goes on where it was, and draws
 * anew once no mark is left. Not the brand moment. `place` names its wait (v1.58.37; 90).
 */
export function OpeningMark({ open = true, place = '' }: { open?: boolean; place?: string }) {
  const [shown, setShown] = useState(() => open || markSince !== undefined);
  markSince ??= shown ? performance.now() : undefined;
  const [ago] = useState(() => ({ '--ago': `${Math.round(performance.now() - (markSince ?? 0))}ms` }) as CSSProperties);
  useEffect(() => () => void setTimeout(() => document.querySelector('.opening-mark') || (markSince = undefined)), []);
  useEffect(() => {
    if (open || !shown) return;
    const t = setTimeout(() => ((markSince = undefined), setShown(false)), still() ? 0 : 200);
    return () => clearTimeout(t);
  }, [open, shown]);
  if (!shown) return null;
  return (
    <div className={open ? 'opening-mark' : 'opening-mark going'} style={ago} aria-hidden="true" data-desyr-opening-mark={place}>
      <svg viewBox="96 62 310 406" height={56}>
        <path pathLength="1" fillRule="evenodd" d={dMark} />
      </svg>
    </div>
  );
}

/**
 * A screen's or page's wait (02, The mark is the loading everywhere, v1.58.37): the opening mark centred
 * in the area that waits, which is busy; after 25 s (unless its load counts them: `timed` false) the
 * too-slow words with Retry (else Reload) centred in its place.
 */
export function MarkWait({ place, retry, timed = true }: { place: string; retry?: () => void; timed?: boolean }) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = timed ? setTimeout(() => setSlow(true), SLOW_MS) : undefined;
    return () => clearTimeout(t);
  }, []);
  return <div className="mark-wait" aria-busy={!slow || undefined}>{slow ? <LoadState status={{ state: 'slow' }} retry={retry} /> : <OpeningMark place={place} />}</div>;
}

/** A full-window page for a problem: the logo, a heading, one line, the main button. */
function ProblemPage({ heading, children, hook = {} }: { heading: string; children: ReactNode; hook?: Record<`data-${string}`, string> }) {
  return (
    <main className="problem-page si" {...hook}>
      <div className="problem" role="alert">
        <Logo size={64} />
        <h1>{heading}</h1>
        {children}
      </div>
    </main>
  );
}

function lineFor(error: unknown, running: boolean): string {
  const message = String((error as Error)?.message ?? '');
  if (/dynamically imported module|Importing a module script failed|error loading dynamically|Unable to preload CSS/i.test(message))
    return t('start.partMissing');
  if (describe(error, 'start').kind === 'NW') return t('start.cantReach');
  return t(running ? 'start.wentWrongLine' : 'start.wentWrongStarting');
}

/** Making or starting the client failed (not a sync failure: those keep trying). */
export function CantStart({ error, onStartFresh }: { error: unknown; onStartFresh: () => void }) {
  const main = useFocusOnShow();
  const [asking, setAsking] = useState(false);
  const failure = describe(error, 'start');
  return (
    <ProblemPage heading={t('start.cantStart')} hook={{ 'data-desyr-start': 'failed' }}>
      <p>{lineFor(error, false)}</p>
      <Button ref={main} variant="filled" onClick={reload}>
        {t('common.tryAgain')}
      </Button>
      {failure.reference && <Reference code={failure.reference} />}
      {asking ? (
        <div className="problem-ask">
          <p>{t('start.startFreshWarning')}</p>
          <div className="start-buttons">
            <Button onClick={() => setAsking(false)}>{t('common.cancel')}</Button>
            <Button variant="critical" onClick={onStartFresh}>
              {t('start.signOut')}
            </Button>
          </div>
        </div>
      ) : (
        <Button variant="text" onClick={() => setAsking(true)}>
          {t('start.startFresh')}
        </Button>
      )}
    </ProblemPage>
  );
}

/** A page that failed while being shown. */
/** Something broke while running (03): the whole page, or (`inPage`) a computer's page card, the rail and side list kept. */
export function Broke({ error, inPage }: { error: unknown; inPage?: boolean }) {
  const main = useFocusOnShow();
  const failure = describe(error, 'general');
  const body = (
    <>
      <p>{lineFor(error, true)}</p>
      <div className="start-buttons">
        <Button ref={main} variant="filled" onClick={reload}>
          {t('common.tryAgain')}
        </Button>
        <Button onClick={() => location.assign(`${import.meta.env.BASE_URL}home/`)}>{t('common.goHome')}</Button>
      </div>
      {failure.reference && <Reference code={failure.reference} />}
    </>
  );
  if (!inPage) return <ProblemPage heading={t('start.wentWrong')}>{body}</ProblemPage>;
  return (
    <div className="problem tab-problem" role="alert" data-desyr-page-problem="">
      <h2>{t('start.wentWrong')}</h2>
      {body}
    </div>
  );
}

/** The frame has been shown: from now on a failure is one while running, never "couldn't start" (03). */
export let running = false;
export const nowRunning = () => void (running = true);

/**
 * A phone's tab page, or a screen opened from a tab, that failed (07, A tab that fails; 03): the
 * problem inside the screen, under its title or back arrow; the tab bar stays.
 */
export function TabProblem({ tab, error, onRetry }: { tab: string; error: unknown; onRetry: () => void }) {
  const main = useFocusOnShow();
  const failure = describe(error, 'general');
  return (
    <div className="problem tab-problem" role="alert" data-desyr-tab-problem={tab}>
      <h2>{t('start.wentWrong')}</h2>
      <p>{lineFor(error, true)}</p>
      <Button ref={main} variant="filled" onClick={onRetry}>
        {t('common.tryAgain')}
      </Button>
      {failure.reference && <Reference code={failure.reference} />}
    </div>
  );
}

/** The session ended while desyr was open or starting. Nothing else of the app stays visible. */
export function SignedOutPage({ onSignInAgain }: { onSignInAgain: () => Promise<void> }) {
  const main = useFocusOnShow();
  const [busy, setBusy] = useState(false);
  // A shared device is never signed straight back in (36, v1.58.51); with desyr ID a sign-out like any
  // other: the generation on and the other tabs told (Behaviour 4.4, v1.58.63).
  useEffect(() => (config().desyrId ? signOutTabs() : markSignedOut()), []);
  return (
    <ProblemPage heading={t('start.signedOut')} hook={{ 'data-desyr-signed-out': '' }}>
      <p>{t('start.signedOutLine')}</p>
      <Button
        ref={main}
        variant="filled"
        busy={busy}
        onClick={() => {
          setBusy(true);
          void onSignInAgain();
        }}
      >
        {t('start.signInAgain')}
      </Button>
    </ProblemPage>
  );
}

/** Started without a network and nobody signed in (03, "Offline start"; 32-install.md). */
export function OfflineStart() {
  const [still, setStill] = useState(false);
  const tryAgain = () => {
    if (navigator.onLine) return dispatchEvent(new Event('online'));
    setStill(true);
    setTimeout(() => setStill(false), 1500);
  };
  return (
    <ProblemPage heading={t('start.offline')} hook={{ 'data-desyr-offline-start': '' }}>
      <p>{t('start.offlineLine')}</p>
      <Button variant="filled" disabled={still} onClick={tryAgain}>
        {t(still ? 'start.stillOffline' : 'common.tryAgain')}
      </Button>
    </ProblemPage>
  );
}

/** The browser refuses local databases: all there is. */
export function NoStorage() {
  return (
    <Splash>
      <Dialog title={t('start.noStorage')} required focusFirst onClose={() => {}}>
        <p className="dialog-text">{t('start.noStorageLine')}</p>
        <DialogButtons>
          <Button variant="filled" onClick={reload}>
            {t('common.tryAgain')}
          </Button>
        </DialogButtons>
      </Dialog>
    </Splash>
  );
}

/** config.json couldn't be loaded: Retry only (the owner dropped "Continue"). */
export function ConfigFailed({ failure, onRetry }: { failure: Failure; onRetry: () => void }) {
  return (
    <Splash>
      <Dialog label={t('start.configFailed')} required focusFirst onClose={() => {}}>
        <p className="dialog-text">{t('start.configFailed')}</p>
        {failure.reference && <Reference code={failure.reference} />}
        <Button variant="critical" className="wide" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      </Dialog>
    </Splash>
  );
}

export type Connection = 'reconnecting' | 'lost' | undefined;

/** The bar above the whole app while the connection is lost; it pushes the app down. */
export function ConnectionBar({ state, onRetry }: { state: Connection; onRetry: () => void }) {
  return (
    <div role="status" className="connection-region">
      {state && (
        <div className={`connection-bar ${state}`} data-desyr-connection-lost="">
          <span>{t(state === 'lost' ? 'connection.lost' : 'connection.reconnecting')}</span>
          <button type="button" className="connection-retry" onClick={onRetry}>
            {t('common.retryNow')}
          </button>
        </div>
      )}
    </div>
  );
}
